import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
} from '@mui/material';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ScrollTable from '../ScrollTable';


const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const { formatMoney: formatCurrency } = useCurrency();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalInvoices, setTotalInvoices] = useState(0);

  useEffect(() => {
    fetchInvoices();
    // Initial load only — paging is driven explicitly by the pagination handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Paged server-side; a long-standing tenant would otherwise only ever see their first
  // 50 invoices, with nothing indicating the rest existed.
  const fetchInvoices = async (targetPage = page, targetSize = rowsPerPage) => {
    try {
      const response = await axios.get('/invoices', {
        params: { page: targetPage + 1, limit: targetSize },
      });
      const body = response.data;
      if (Array.isArray(body)) {
        setInvoices(body);
        setTotalInvoices(body.length);
      } else {
        setInvoices(body.data || []);
        setTotalInvoices(body.total ?? (body.data || []).length);
      }
    } catch (error) {
      toast.error('Error fetching invoices');
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchInvoices(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    setPage(0);
    fetchInvoices(0, size);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'success',
      partial: 'info',
      partially_paid: 'info',
      fully_paid: 'success',
      overdue: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      paid: 'Paid',
      partial: 'Partial',
      partially_paid: 'Partially Paid',
      fully_paid: 'Fully Paid',
      overdue: 'Overdue',
      cancelled: 'Cancelled',
    };
    return labels[status] || (status || '').replace(/_/g, ' ');
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Invoices
      </Typography>
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Invoice Number</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No invoices found
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice._id}>
                  <TableCell>{invoice.invoiceNumber}</TableCell>
                  <TableCell>
                    {format(new Date(invoice.invoiceDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {format(new Date(invoice.dueDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={invoice.balance > 0 ? 'error' : 'success'}
                      fontWeight="bold"
                    >
                      {formatCurrency(invoice.balance || invoice.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(invoice.status)}
                      color={getStatusColor(invoice.status)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>
      <TablePagination
        component="div"
        count={totalInvoices}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Paper>
  );
};

export default Invoices;

